import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

interface VercelStatus {
  connected: boolean;
  username?: string;
  email?: string;
  teamId?: string;
  teamName?: string;
  connectedAt?: string;
  error?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

/**
 * Get Vercel authorization URL
 */
export const getAuthUrl = async (returnUrl?: string): Promise<ApiResponse<{ authUrl: string }>> => {
  const params = returnUrl ? { returnUrl } : {};
  const response = await api.get('/vercel/authorize', { params });
  return response.data;
};

/**
 * Get Vercel connection status
 */
export const getStatus = async (): Promise<ApiResponse<VercelStatus>> => {
  const response = await api.get('/vercel/status');
  return response.data;
};

/**
 * Disconnect Vercel account
 */
export const disconnect = async (): Promise<ApiResponse<void>> => {
  const response = await api.delete('/vercel/disconnect');
  return response.data;
};

/**
 * Exchange code for access token (after Vercel redirect)
 */
export const exchangeCode = async (code: string, configurationId?: string, teamId?: string): Promise<ApiResponse<VercelStatus>> => {
  const response = await api.post('/vercel/exchange', { code, configurationId, teamId });
  return response.data;
};

/**
 * Open Vercel OAuth flow in same window
 */
export const connectVercel = async (): Promise<void> => {
  try {
    // Pass current URL so we return here after OAuth
    const returnUrl = window.location.href;
    const response = await getAuthUrl(returnUrl);
    if (response.success && response.data?.authUrl) {
      // Open in same window (Vercel will redirect back to returnUrl)
      window.location.href = response.data.authUrl;
    } else {
      throw new Error(response.message || 'Failed to get authorization URL');
    }
  } catch (error) {
    console.error('Connect Vercel error:', error);
    throw error;
  }
};

export default {
  getAuthUrl,
  getStatus,
  disconnect,
  connectVercel,
  exchangeCode
};
